'use client'

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Transition, TransitionChild } from '@headlessui/react'
import { ChevronDown, ChevronRight, Heart, Menu, MoveRight, Search, ShieldAlert, ShoppingBag, X } from 'lucide-react'
import { formatImageURL, formatPrice, getBuyXTriggerCount, getBuyXGetYRewardRule, getCartTotal, getFontClass, getProductDisplayPrice, isCustomerAuthActive } from '@/utils/util'
import { useSession } from 'next-auth/react'

import { usePathname, useSearchParams } from 'next/navigation'
import { loginHrefFrom, navigateUrl } from '@/utils/client-request'
import Skeleton from '@/components/elements/Skeleton'
import { useCart, useSearch, useNavigation } from '@/lib/commerce/hooks'
import CartItemSidebar from '../cart/CartItemSidebar'
import SidePanel from '../elements/SidePanel'
import EmptyState from '../elements/EmptyState'

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `h-12 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const outlinedButton = `h-12 border border-foreground text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${focusRing}`
const heroHeader = 'in-[body:has([data-hero])]:bg-transparent in-[body:has([data-hero])]:text-white in-[body:has([data-hero])]:border-transparent data-[solid=true]:bg-background data-[solid=true]:text-foreground data-[solid=true]:border-border'
const heroLogo = 'group-data-[solid=false]/header:in-[body:has([data-hero])]:[filter:url(#fashion-hero-logo)]'
const sweep = 'pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] transition-[background-size] duration-500 ease-out motion-reduce:transition-none'
const sweepLine = (active: boolean) => `${sweep} ${active ? 'bg-size-[100%_1px]' : 'bg-size-[0%_1px] group-hover:bg-size-[100%_1px] group-focus-visible:bg-size-[100%_1px]'}`

export default function Header({ vendor, categories, subscription, campaigns, mobileMenuOpen, setMobileMenuOpen }: { vendor?: any, categories?: any, subscription?: any, campaigns?: any, mobileMenuOpen: boolean, setMobileMenuOpen: (open: boolean) => void }) {
  const { data: session } = useSession()

  const { initialLoading, cartCount, cart, validateCart, isValidating, canCheckout, clearCart, buyXGetYInfo, addRewardItem, availableOrderRewardItems, rewardLoading, freeShippingProgress, amount, sale, activeOrderDiscount, orderDiscountAmount, appliedCoupon, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, loyaltyDiscountAmount } = useCart()
  const { query, setQuery, results: searchResults, categories: searchCategories, loading: isSearching, history: searchHistory, clearHistory: clearSearchHistory, runSearch } = useSearch()
  const { navigation } = useNavigation({ vendor, subscription, campaigns, categories, icons: { loyalty: true } })
  const sections = navigation.catalog.sections
  const headerSections = sections.slice(0, 4)
  const pathname = usePathname()
  const saleLink = navigation.links.find((link: any) => link.isSale)
  const campaignLinks = navigation.links.filter((link: any) => link.isCampaign)
  const otherLinks = navigation.links.filter((link: any) => !link.isCampaign && !link.isSale)
  const isCampaignActive = (link: any) => !!pathname?.endsWith(link.href)
  const searchParams = useSearchParams()

  const [openCart, setOpenCart] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [openSectionId, setOpenSectionId] = useState<string | null>(null)

  const searchParam = searchParams?.get('search') || ''
  const [prevSearchParam, setPrevSearchParam] = useState('')
  if (searchParam !== prevSearchParam) {
    setPrevSearchParam(searchParam)
    if (searchParam) setQuery(searchParam)
  }

  const cartItems = cart?.products || []
  const cartTotal = useMemo(() => {
    const total = getCartTotal(cartItems, !!session?.user?.id)
    const remaining = Math.max(0, total - (orderDiscountAmount || 0))
    const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, remaining) : 0
    return Math.max(0, total - (orderDiscountAmount || 0) - loyaltyDiscount)
  }, [cartItems, session?.user?.id, loyaltyDiscountAmount, orderDiscountAmount])

  const handleCartOpen = async () => {
    setOpenCart(true)
    if (cartCount > 0 && !isValidating) {
      try {
        await validateCart()
      } catch (error) {
        console.error('Error validating cart:', error)
      }
    }
  }

  const handleCartClose = () => {
    setOpenCart(false)
  }

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openSectionPanel = (id: string) => {
    cancelClose()
    setOpenSectionId(id)
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenSectionId(null), 120)
  }
  const openSection = sections.find((section: any) => section.id === openSectionId && section.items?.length > 0)

  const activeCategory = pathname?.includes('/product') ? searchParams?.get('category') : null
  const isAllProducts = /\/product\/?$/.test(pathname || '') && !activeCategory
  const isActiveSection = (section: any) => !!activeCategory && section.href.endsWith(`category=${activeCategory}`)

  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        setScrolled(window.scrollY > 8)
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  const fontClass = getFontClass(vendor?.font)
  const showAuth = isCustomerAuthActive(vendor)
  const showCart = vendor?.canPlaceOrder !== false
  const hasQuery = query.trim().length > 0
  const wishlistHref = navigateUrl(session?.user?.id ? '/wishlist' : '/login?url=/wishlist', pathname)
  const visibleCartCount = initialLoading ? 0 : cartCount

  const progressNote = (label: React.ReactNode, value: React.ReactNode, pct: number) => (
    <div className="mb-6">
      <div className="flex items-baseline justify-between gap-4 text-[13px]">
        <span>{label}</span>
        <span className="shrink-0 text-muted-foreground tabular-nums">{value}</span>
      </div>
      <div className="mt-2 h-0.5 bg-border">
        <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(pct, 100))}%` }} />
      </div>
    </div>
  )

  const rewardRow = (reward: any, rewardPrice: number, label: string, disabled: boolean, onAdd: () => void) => (
    <div key={reward.id} className="flex items-center gap-3 py-3">
      <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-12 aspect-3/4 object-cover bg-muted shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] line-clamp-1">{reward.name}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs tabular-nums">
          <span>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
          {reward.price && rewardPrice < reward.price && <span className="line-through text-muted-foreground">{formatPrice(reward.price)}</span>}
          {reward.valueType === 'percent' && reward.valuePercent && <span className="text-muted-foreground">-{reward.valuePercent}%</span>}
        </div>
      </div>
      <button type="button" onClick={onAdd} disabled={disabled} className={`h-9 px-4 border border-foreground text-xs tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground ${focusRing}`}>
        {rewardLoading && label === 'Нэмэх' ? 'Нэмж байна…' : label}
      </button>
    </div>
  )

  const logo = vendor?.logo ? (
    <img src={formatImageURL(vendor.logo)} alt={vendor?.name || 'logo'} className={`h-7 lg:h-8 w-auto max-w-40 object-contain ${heroLogo}`} />
  ) : (
    <span className="text-lg font-medium tracking-tight">{vendor?.name}</span>
  )

  const searchContent = !hasQuery ? (
    <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
      {searchHistory.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className={eyebrow}>Сүүлд хайсан</h3>
            <button type="button" onClick={clearSearchHistory} className={`text-[11px] text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>Цэвэрлэх</button>
          </div>
          <ul className="space-y-2">
            {searchHistory.map(term => (
              <li key={term}>
                <button type="button" onClick={() => runSearch(term)} className={`text-sm text-left transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>{term}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {sections.length > 0 && (
        <div className={searchHistory.length > 0 ? '' : 'lg:col-span-2'}>
          <h3 className={`${eyebrow} mb-3`}>Ангилал</h3>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {sections.map((section: any) => (
              <li key={section.id}>
                <a href={section.href} className={`text-sm transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>{section.name}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  ) : isSearching ? (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-6">
      {[...Array(6)].map((_, i) => (
        <div key={i}>
          <Skeleton className="aspect-[3/4] w-full" />
          <Skeleton className="h-3 w-3/4 mt-3" />
          <Skeleton className="h-3 w-1/3 mt-2" />
        </div>
      ))}
    </div>
  ) : (searchResults.length > 0 || searchCategories.length > 0) ? (
    <div className="space-y-8">
      {searchCategories.length > 0 && (
        <div>
          <h3 className={`${eyebrow} mb-3`}>Ангилал</h3>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {searchCategories.map((cat: any) => (
              <li key={cat.id}>
                <a href={navigateUrl(`/product?category=${cat.slug || cat.id}`, pathname)} className={`text-sm transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>{cat.name}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {searchResults.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <h3 className={eyebrow}>Бүтээгдэхүүн</h3>
            <button type="button" onClick={() => runSearch()} className={`inline-flex items-center gap-1.5 text-[13px] transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>
              Бүх илэрц
              <MoveRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-6">
            {searchResults.slice(0, 6).map((product: any) => {
              const variant = product?.variant
              const imageUrl = variant?.images?.length > 0 ? formatImageURL(variant.images[0]) : formatImageURL('placeholder.png')
              const price = getProductDisplayPrice(product, variant, !!session?.user?.id)
              return (
                <li key={product.id}>
                  <a href={navigateUrl(`/product/${product.customId || product.id}`, pathname)} className={`group block ${focusRing}`}>
                    <div className="aspect-[3/4] bg-muted overflow-hidden">
                      <img src={imageUrl} alt={product?.name} className="h-full w-full object-cover transition-opacity duration-150 group-hover:opacity-90" />
                    </div>
                    <div className="mt-3 text-[13px] leading-snug line-clamp-2">{product?.name}</div>
                    <div className="mt-1 text-[13px] text-muted-foreground tabular-nums">{formatPrice(price)}</div>
                  </a>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  ) : (
    <p className="text-sm text-muted-foreground">&ldquo;{query}&rdquo; гэсэн илэрц олдсонгүй</p>
  )

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-40">
        <header data-solid={scrolled || !!openSection} className={`group/header w-full bg-background text-foreground border-b border-border transition-colors duration-300 ${heroHeader} ${fontClass}`}>
          <svg aria-hidden className="absolute size-0 overflow-hidden">
            <filter id="fashion-hero-logo" colorInterpolationFilters="sRGB">
              <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 -0.4252 -1.4304 -0.1444 2 0" />
            </filter>
          </svg>
          <div className="relative" onKeyDown={e => { if (e.key === 'Escape') setOpenSectionId(null) }}>
            <div className="w-full px-4 lg:px-6">
              <div className="flex lg:hidden h-14 items-center justify-between gap-4">
                <a href={navigateUrl('/', pathname)} className={`flex items-center min-w-0 ${focusRing}`}>{logo}</a>
                <div className="flex items-center -mr-2">
                  <button type="button" onClick={() => setSearchOpen(true)} className={`h-10 w-10 grid place-items-center ${focusRing}`} aria-label="Хайх">
                    <Search className="size-4.5" strokeWidth={1.5} />
                  </button>
                  {showAuth && (
                    <a href={wishlistHref} className={`h-10 w-10 grid place-items-center ${focusRing}`} aria-label="Хүслийн жагсаалт">
                      <Heart className="size-4.5" strokeWidth={1.5} />
                    </a>
                  )}
                  <button type="button" onClick={() => setMobileMenuOpen(true)} className={`h-10 w-10 grid place-items-center ${focusRing}`} aria-label="Цэс">
                    <Menu className="h-5 w-5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center h-16 gap-10">
                <a href={navigateUrl('/', pathname)} className={`justify-self-start flex items-center ${focusRing}`}>{logo}</a>

                <nav aria-label="Ангилал" className="max-w-[56vw] overflow-x-auto scrollbar-hide" onMouseLeave={scheduleClose}>
                  <ul className="flex items-center gap-7 h-16">
                    {campaignLinks.map((link: any) => (
                      <li key={link.href} className="h-full">
                        <a href={navigateUrl(link.href, pathname)} onMouseEnter={scheduleClose} onFocus={scheduleClose} className={`group h-full flex items-center whitespace-nowrap text-[13px] tracking-wide ${focusRing}`}>
                          <span className={sweepLine(isCampaignActive(link))}>{link.name}</span>
                        </a>
                      </li>
                    ))}
                    <li className="h-full">
                      <a href={navigateUrl('/product', pathname)} onMouseEnter={scheduleClose} onFocus={scheduleClose} className={`group h-full flex items-center whitespace-nowrap text-[13px] tracking-wide ${focusRing}`}>
                        <span className={sweepLine(isAllProducts)}>Бүх бараа</span>
                      </a>
                    </li>
                    {headerSections.map((section: any) => (
                      <li key={section.id} className="h-full">
                        <a
                          href={section.href}
                          onMouseEnter={() => openSectionPanel(section.id)}
                          onFocus={() => openSectionPanel(section.id)}
                          className={`group h-full flex items-center whitespace-nowrap text-[13px] tracking-wide ${focusRing}`}
                        >
                          <span className={sweepLine(openSectionId === section.id || isActiveSection(section))}>{section.name}</span>
                        </a>
                      </li>
                    ))}
                    {saleLink && (
                      <li className="h-full">
                        <a href={navigateUrl(saleLink.href, pathname)} onMouseEnter={scheduleClose} onFocus={scheduleClose} className={`group h-full flex items-center whitespace-nowrap text-[13px] tracking-wide ${focusRing}`}>
                          <span className={sweepLine(activeCategory === 'sale')}>{saleLink.name}</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </nav>

                <div className="justify-self-end flex items-center gap-7 text-[13px] tracking-wide">
                  <button type="button" onClick={() => setSearchOpen(true)} className={`h-10 w-10 -mx-3 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${focusRing}`} aria-label="Хайх">
                    <Search className="size-4.5" strokeWidth={1.5} />
                  </button>
                  {showAuth && (
                    <a href={wishlistHref} className={`h-10 w-10 -mx-3 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${focusRing}`} aria-label="Хүслийн жагсаалт">
                      <Heart className="size-4.5" strokeWidth={1.5} />
                    </a>
                  )}
                  {showAuth && (
                    <a href={session?.user?.id ? navigateUrl('/profile', pathname) : loginHrefFrom(pathname)} className={`whitespace-nowrap transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>
                      {session?.user?.id ? (session.user.firstName || 'Профайл') : 'Нэвтрэх'}
                    </a>
                  )}
                  {showCart && (
                    <button type="button" onClick={handleCartOpen} aria-label={visibleCartCount > 0 ? `Сагс, ${visibleCartCount} бараа` : 'Сагс'} className={`inline-flex items-start whitespace-nowrap transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>
                      Сагс
                      {visibleCartCount > 0 && (
                        <span className="ml-0.5 -mt-1 text-[10px] leading-none tabular-nums">{visibleCartCount > 99 ? '99+' : visibleCartCount}</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {openSection && (
              <div className="hidden lg:block absolute inset-x-0 top-full bg-background border-y border-border" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                <div className="w-full px-6 py-10 grid grid-cols-[200px_1fr] gap-10">
                  <div>
                    <div className="text-[13px] font-medium">{openSection.name}</div>
                    <a href={openSection.href} className={`mt-3 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>
                      Бүгдийг үзэх
                      <MoveRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </a>
                  </div>
                  <div className="grid grid-cols-4 gap-x-10 gap-y-8">
                    {openSection.items.map((item: any) => (
                      <div key={item.name} className="min-w-0">
                        <a href={item.href} className={`text-[13px] font-medium transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>{item.name}</a>
                        {item.children?.length > 0 && (
                          <ul className="mt-3 space-y-2">
                            {item.children.map((child: any) => (
                              <li key={child.name}>
                                <a href={child.href} className={`text-[13px] text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>{child.name}</a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {vendor?.isDanVerified === false && (
            <div className="bg-amber-50 border-t border-amber-200">
              <div className="w-full px-4 lg:px-6 h-9 flex items-center justify-between text-[12.5px]">
                <div className="flex items-center gap-2 text-amber-900 min-w-0">
                  <ShieldAlert className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                  <span className="truncate">
                    <span className="font-semibold">Энэ дэлгүүр баталгаажаагүй байна.</span>
                    <span className="hidden sm:inline opacity-80"> Худалдан авалт хийхдээ анхаарна уу.</span>
                  </span>
                </div>
                <a href={navigateUrl('/status', pathname)} className="inline-flex items-center gap-1 text-amber-900 font-semibold hover:underline shrink-0">
                  Дэлгэрэнгүй
                  <ChevronRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}
        </header>
      </div>

      <Transition show={searchOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setSearchOpen}>
          <TransitionChild as={Fragment} enter="transition-opacity ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-foreground/20" />
          </TransitionChild>
          <div className="fixed inset-x-0 top-0">
            <TransitionChild as={Fragment} enter="transition ease-out duration-200 motion-reduce:transition-none" enterFrom="opacity-0 -translate-y-3" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-150 motion-reduce:transition-none" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 -translate-y-3">
              <DialogPanel className={`bg-background text-foreground border-b border-border ${fontClass}`}>
                <div className="w-full px-4 lg:px-6">
                  <div className="h-14 lg:h-16 flex items-center gap-3 border-b border-border">
                    <Search className="size-4.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                    <input
                      autoFocus
                      type="search"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') runSearch() }}
                      placeholder="Юу хайж байна вэ?"
                      aria-label="Хайх"
                      className="flex-1 min-w-0 h-full bg-transparent border-0 outline-none text-base lg:text-lg focus:outline-none focus:ring-0 placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
                    />
                    <button type="button" onClick={() => setSearchOpen(false)} className={`h-10 w-10 -mr-2 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${focusRing}`} aria-label="Хаах">
                      <X className="h-5 w-5" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="py-6 lg:py-8 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
                    {searchContent}
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      <SidePanel
        open={openCart}
        onClose={handleCartClose}
        title="Сагс"
        subtitle={!initialLoading && cartCount > 0 && (
          <div className="flex items-center justify-between gap-4 text-[13px]">
            <span className="text-muted-foreground tabular-nums">{cartCount} бүтээгдэхүүн</span>
            <button type="button" onClick={clearCart} className={`text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>Сагс хоослох</button>
          </div>
        )}
        footer={cartCount > 0 && (
          <div className="space-y-4">
            {vendor?.orderMinAmount > 0 && (amount - sale) < vendor.orderMinAmount && progressNote('Захиалгын доод дүн', `${formatPrice(vendor.orderMinAmount - (amount - sale))} дутуу`, ((amount - sale) / vendor.orderMinAmount) * 100)}
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span>Нийт дүн</span>
              <span className="tabular-nums">{formatPrice(cartTotal)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => { setOpenCart(false); window.location.href = navigateUrl('/cart', pathname) }} className={outlinedButton}>Сагс үзэх</button>
              <button
                type="button"
                disabled={!canCheckout()}
                onClick={() => {
                  setOpenCart(false)
                  if (session?.user?.id || vendor?.canGuestOrder === true || !isCustomerAuthActive(vendor)) {
                    window.location.href = navigateUrl('/checkout', pathname)
                  } else {
                    window.location.href = navigateUrl('/checkout/login', pathname)
                  }
                }}
                className={solidButton}
              >
                Худалдан авах
              </button>
            </div>
          </div>
        )}
      >
        {freeShippingProgress && !cartItems.every((p: any) => p.isDigital === true) && progressNote('Үнэгүй хүргэлт', freeShippingProgress.reached ? 'Хангагдсан' : `${formatPrice(freeShippingProgress.remaining)} дутуу`, freeShippingProgress.percentage)}
        {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
          const subPrice = amount - sale
          const minAmt = activeOrderDiscount.minOrderAmount
          const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
          if (!target) return null
          const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
          if (!discountLabel) return null
          const reached = subPrice >= minAmt
          return progressNote(`${formatPrice(minAmt)}-с дээш захиалгад ${discountLabel}`, reached ? `-${formatPrice(orderDiscountAmount)}` : `${formatPrice(minAmt - subPrice)} дутуу`, (subPrice / minAmt) * 100)
        })()}

        {initialLoading ? (
          <div className="divide-y divide-border">
            {[0, 1].map(i => (
              <div key={i} className="flex gap-4 py-5 first:pt-0">
                <Skeleton className="w-20 aspect-3/4 rounded-none" />
                <div className="flex-1 space-y-2 pt-1">
                  <Skeleton className="h-3.5 w-3/4 rounded-none" />
                  <Skeleton className="h-3.5 w-1/3 rounded-none" />
                </div>
              </div>
            ))}
          </div>
        ) : cartCount > 0 && cartItems?.length > 0 ? (
          <>
            <div className="divide-y divide-border">
              {cartItems.map((product: any) => (
                <div key={`${product.isRewardItem ? 'reward-' : ''}${product.id}-${product.variant?.id}`} className="py-5 first:pt-0">
                  <CartItemSidebar product={product} pathname={pathname} />
                </div>
              ))}
            </div>

            {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
              const firstState = getBuyXGetYRewardState(info, info.rewardProducts[0])
              const totalRemaining = firstState.totalRemaining
              if (totalRemaining <= 0) return null
              return (
                <div key={info.discount.id} className="mt-6 border border-border p-4">
                  <div className={eyebrow}>{info.discount?.name || 'Хөнгөлөлт'}</div>
                  <p className="mt-1.5 text-[13px]">{getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Нөхцөл хангагдлаа ({info.triggerCount}/{getBuyXTriggerCount(info.discount)}) — {totalRemaining} урамшуулалтай бараа авах боломжтой</p>
                  <div className="mt-3 divide-y divide-border">
                    {info.rewardProducts.map((reward: any) => {
                      const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                      if (isLockedByOther) return null
                      return rewardRow(reward, rewardPrice, isOutOfStock ? 'Дууссан' : canAdd ? 'Нэмэх' : 'Нэмэгдсэн', !canAdd || rewardLoading, () => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY'))
                    })}
                  </div>
                </div>
              )
            })())}

            {availableOrderRewardItems?.length > 0 && (
              <div className="mt-6 border border-border p-4">
                <div className={eyebrow}>Захиалгын бэлэг</div>
                <p className="mt-1.5 text-[13px]">Захиалгын хөнгөлөлтийн бэлэг</p>
                <div className="mt-3 divide-y divide-border">
                  {availableOrderRewardItems.map((reward: any) => {
                    const isOutOfStock = isRewardOutOfStock(reward)
                    return rewardRow(reward, getRewardPrice(reward), isOutOfStock ? 'Дууссан' : 'Нэмэх', isOutOfStock || rewardLoading, () => addRewardItem(reward, 'order'))
                  })}
                </div>
              </div>
            )}

            {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
              <div className="mt-6 border-l border-foreground pl-4">
                <p className="text-[13px]">Үлдэгдэл дууссан бараа</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү</p>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon={ShoppingBag} title="Таны сагс хоосон байна" description="Таалагдсан бүтээгдэхүүнээ сагсандаа нэмээрэй.">
            <a href={navigateUrl('/product', pathname)} onClick={() => setOpenCart(false)} className={`inline-flex items-center ${outlinedButton} px-8`}>Дэлгүүр хэсэх</a>
          </EmptyState>
        )}
      </SidePanel>

      <Transition show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
          <TransitionChild as={Fragment} enter="transition-opacity ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-foreground/20" />
          </TransitionChild>
          <div className="fixed inset-0 flex justify-end">
            <TransitionChild as={Fragment} enter="transition ease-out duration-200 transform motion-reduce:transition-none" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in duration-150 transform motion-reduce:transition-none" leaveFrom="translate-x-0" leaveTo="translate-x-full">
              <DialogPanel className={`flex w-[86%] max-w-sm flex-col bg-background text-foreground ${fontClass}`}>
                <div className="h-14 px-4 flex items-center justify-between border-b border-border shrink-0">
                  <span className={eyebrow}>Цэс</span>
                  <button type="button" onClick={() => setMobileMenuOpen(false)} className={`h-10 w-10 -mr-2 grid place-items-center ${focusRing}`} aria-label="Хаах">
                    <X className="h-5 w-5" strokeWidth={1.5} />
                  </button>
                </div>
                <nav aria-label="Цэс" className="flex-1 overflow-y-auto overscroll-contain px-4">
                  <ul>
                    {campaignLinks.map((link: any) => (
                      <li key={link.href} className="border-b border-border">
                        <a href={navigateUrl(link.href, pathname)} onClick={() => setMobileMenuOpen(false)} className={`flex items-center min-h-16 py-3 text-xl tracking-tight ${focusRing}`}>{link.name}</a>
                      </li>
                    ))}
                    <li className="border-b border-border">
                      <a href={navigateUrl('/product', pathname)} onClick={() => setMobileMenuOpen(false)} className={`flex items-center h-14 text-[15px] ${focusRing}`}>Бүх бараа</a>
                    </li>
                    {sections.length > 0 && (
                      <li className="border-b border-border">
                        <Disclosure>
                          {({ open }) => (
                            <>
                              <DisclosureButton className={`w-full h-14 flex items-center justify-between text-[15px] text-left ${focusRing}`}>
                                Ангилал
                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-150 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                              </DisclosureButton>
                              <DisclosurePanel as="ul" className="pb-4 pl-4 border-l border-border mb-4">
                                {sections.map((section: any) => (
                                  <li key={section.id}>
                                    {section.items?.length > 0 ? (
                                      <Disclosure>
                                        {({ open: sectionOpen }) => (
                                          <>
                                            <DisclosureButton className={`w-full h-11 flex items-center justify-between text-sm text-left ${focusRing}`}>
                                              {section.name}
                                              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 ${sectionOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                                            </DisclosureButton>
                                            <DisclosurePanel as="ul" className="pb-3 pl-3 space-y-2.5">
                                              <li>
                                                <a href={section.href} onClick={() => setMobileMenuOpen(false)} className={`text-sm text-muted-foreground ${focusRing}`}>Бүгдийг үзэх</a>
                                              </li>
                                              {section.items.map((item: any) => (
                                                <li key={item.name}>
                                                  <a href={item.href} onClick={() => setMobileMenuOpen(false)} className={`text-sm ${focusRing}`}>{item.name}</a>
                                                  {item.children?.length > 0 && (
                                                    <ul className="mt-2 pl-3 space-y-2 border-l border-border">
                                                      {item.children.map((child: any) => (
                                                        <li key={child.name}>
                                                          <a href={child.href} onClick={() => setMobileMenuOpen(false)} className={`text-sm text-muted-foreground ${focusRing}`}>{child.name}</a>
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  )}
                                                </li>
                                              ))}
                                            </DisclosurePanel>
                                          </>
                                        )}
                                      </Disclosure>
                                    ) : (
                                      <a href={section.href} onClick={() => setMobileMenuOpen(false)} className={`flex items-center h-11 text-sm ${focusRing}`}>{section.name}</a>
                                    )}
                                  </li>
                                ))}
                              </DisclosurePanel>
                            </>
                          )}
                        </Disclosure>
                      </li>
                    )}
                    {saleLink && (
                      <li className="border-b border-border">
                        <a href={navigateUrl(saleLink.href, pathname)} onClick={() => setMobileMenuOpen(false)} className={`flex items-center h-14 text-[15px] ${focusRing}`}>{saleLink.name}</a>
                      </li>
                    )}
                    {otherLinks.length > 0 && (
                      <li className="pt-10 pb-3">
                        <span className={eyebrow}>Дэлгүүр</span>
                      </li>
                    )}
                    {otherLinks.map((link: any) => (
                      <li key={link.href}>
                        <a href={navigateUrl(link.href, pathname)} onClick={() => setMobileMenuOpen(false)} className={`flex items-center h-11 text-[15px] text-muted-foreground transition-colors duration-150 hover:text-foreground ${focusRing}`}>{link.name}</a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      <div aria-hidden className="invisible pointer-events-none in-[body:has([data-hero])]:hidden">
        <div className="h-14 lg:h-16 border-b border-border" />
        {vendor?.isDanVerified === false && <div className="h-9 border-t border-amber-200" />}
      </div>
    </>
  )
}
